import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { internalChatEndpoint } from "@/config/endpoints";

const THREADS_PAGE_SIZE = 20;

interface ThreadsPage {
  threads: ChatThread[];
  hasMore: boolean;
}

export interface ChatThread {
  leadId: number;
  leadName: string;
  phone: string;
  stageName?: string | null;
  statusName?: string | null;
  subStatusName?: string | null;
  children: {
    id: number;
    name: string;
    users: { id: number; name: string }[];
  }[];
  leadUsers: { id: number; name: string; role: string }[];
  lastMessage: string;
  lastMessageAt: string;
  senderName: string;
  unreadCount: number;
}

// Push-only — no `refetchInterval`. Updates flow from socket events
// (`INTERNAL_CHAT_NEW` / `INTERNAL_CHAT_READ`) which `useGlobalSocketEvents`
// already wires to `queryClient.invalidateQueries(["chat-threads"])` and
// `["chat-unread-count"]`. The previous 60s/30s polls duplicated that work
// and generated ~90 background req/min per 30 active users.
//
// `refetchOnWindowFocus: true` is the safety net: if the socket misses an
// event during a brief disconnect, focusing the tab re-fetches the truth.
// Combined with the React Query default `refetchOnReconnect: true`, missed
// events are recovered within a focus or network blip.
export function useChatThreads(enabled = true) {
  const query = useInfiniteQuery({
    queryKey: ["chat-threads"],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await axiosInstance.get(`${internalChatEndpoint}/threads`, {
        params: { page: pageParam, limit: THREADS_PAGE_SIZE },
      });
      return {
        threads: (res.data?.threads || []) as ChatThread[],
        hasMore: Boolean(res.data?.hasMore),
      } as ThreadsPage;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: ThreadsPage, allPages) =>
      lastPage.hasMore ? allPages.length + 1 : undefined,
    enabled,
    staleTime: 1000 * 60 * 5, // 5min — socket invalidates earlier when there's actually a change
    refetchOnWindowFocus: true,
  });

  // Flatten every loaded page into a single thread list. Socket-driven
  // `invalidateQueries(["chat-threads"])` refetches all loaded pages, so live
  // updates keep working unchanged.
  const threads = query.data?.pages.flatMap((p) => p.threads) ?? [];

  return {
    threads,
    isLoading: query.isLoading,
    refetch: query.refetch,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}

export function useUnreadChatCount() {
  const query = useQuery<number>({
    queryKey: ["chat-unread-count"],
    queryFn: async () => {
      const res = await axiosInstance.get(
        `${internalChatEndpoint}/unread-count`
      );
      return res.data?.count || 0;
    },
    staleTime: 1000 * 60 * 5, // 5min — socket invalidates earlier when there's actually a change
    refetchOnWindowFocus: true,
  });

  return query.data || 0;
}
